import { apiClient } from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import type { EmployeeTimeOffBalances } from '../types/employeeTimeOffBalance';
import { computeSignedLeaveAvailable } from '../utils/leaveBalance';

type Envelope<T> = { success: boolean; message: string; data: T };
const n = (v: unknown) => Number(v || 0);

export async function getEmployeeTimeOffBalances(
  employeeId: number,
  year: number,
  month: number,
): Promise<EmployeeTimeOffBalances> {
  const r = await apiClient.get<Envelope<EmployeeTimeOffBalances>>(
    API_ENDPOINTS.EMPLOYEES.TIME_OFF_BALANCES(employeeId),
    { params: { year, month } },
  );
  // A successful response with no rows is "no data", not an error — tolerate
  // missing sections so empty balances never surface as a failure.
  const d: Partial<EmployeeTimeOffBalances> = r.data?.data ?? {};
  const leave = Array.isArray(d.leave) ? d.leave : [];
  const permissions = Array.isArray(d.permissions) ? d.permissions : [];
  return {
    leave: leave.map(x => {
      const openingBalance = n(x.openingBalance);
      const creditedDays = n(x.creditedDays);
      const debitedDays = n(x.debitedDays);
      const adjustedDays = n(x.adjustedDays);
      const pendingLeaveDays = n(x.pendingLeaveDays);
      const availableBalance =
        computeSignedLeaveAvailable({
          openingBalance,
          creditedDays,
          adjustedDays,
          debitedDays,
          pendingLeaveDays,
          availableBalance: x.availableBalance,
        }) ?? 0;
      return {
        ...x,
        employeeLeaveBalanceId: n(x.employeeLeaveBalanceId),
        leaveTypeId: n(x.leaveTypeId),
        leaveYear: n(x.leaveYear),
        openingBalance,
        creditedDays,
        debitedDays,
        adjustedDays,
        pendingLeaveDays,
        availableBalance,
        closingBalance: n(x.closingBalance),
        annualEntitlement: x.annualEntitlement == null ? null : n(x.annualEntitlement),
        monthlyAccrual: x.monthlyAccrual == null ? null : n(x.monthlyAccrual),
      };
    }),
    wfh: d.wfh
      ? {
          ...d.wfh,
          wfhPolicyId: n(d.wfh.wfhPolicyId),
          maximumDaysPerMonth: n(d.wfh.maximumDaysPerMonth),
          maximumDaysPerYear: n(d.wfh.maximumDaysPerYear),
          usedDaysMonth: n(d.wfh.usedDaysMonth),
          usedDaysYear: n(d.wfh.usedDaysYear),
          remainingDaysMonth: n(d.wfh.remainingDaysMonth),
          remainingDaysYear: n(d.wfh.remainingDaysYear),
        }
      : null,
    permissions: permissions.map(x => ({
      ...x,
      permissionTypeId: n(x.permissionTypeId),
      permissionPolicyId: n(x.permissionPolicyId),
      maximumPermissionsPerDay: n(x.maximumPermissionsPerDay),
      maximumPermissionsPerMonth: n(x.maximumPermissionsPerMonth),
      maximumPermissionsPerYear: n(x.maximumPermissionsPerYear),
      usedMonth: n(x.usedMonth),
      usedYear: n(x.usedYear),
      remainingMonth: n(x.remainingMonth),
      remainingYear: n(x.remainingYear),
    })),
  };
}
